/**
 * El bloqueo rápido con PIN.
 *
 * ---------------------------------------------------------------------------
 *  Qué es esto y qué NO es
 * ---------------------------------------------------------------------------
 *
 * **Es una pantalla de bloqueo local.** Sirve para que quien coja tu teléfono
 * desbloqueado no vea tus cifras de un vistazo. Eso es todo.
 *
 * **NO es una credencial de acceso y no sustituye a la contraseña.** El PIN no viaja
 * nunca al servidor y no sirve para entrar desde otro dispositivo. Vive aquí, en este
 * navegador, y solo tapa la pantalla.
 *
 * Esa distinción importa y se dice en la propia pantalla de ajustes. Una aplicación
 * que llama «seguridad» a un PIN de cuatro dígitos le está mintiendo a quien la usa, y
 * esa mentira se paga el día que alguien confía en ella de verdad.
 *
 * Lo tenía Cuentify. La diferencia es que aquí se explica qué protege y qué no.
 *
 * ---------------------------------------------------------------------------
 *  Cómo se guarda
 * ---------------------------------------------------------------------------
 *
 * **El PIN no se guarda.** Se guarda su derivación con PBKDF2, 200.000 vueltas y una
 * sal aleatoria distinta por dispositivo. Comprobar el PIN son unos 100 ms de cálculo:
 * suficiente para que probar 10.000 combinaciones a mano sea inviable.
 *
 * Sin las 200.000 vueltas, un PIN de cuatro dígitos se rompe en microsegundos leyendo
 * el almacenamiento del navegador. La diferencia entre 4 dígitos seguros y 4 dígitos
 * inútiles está justo ahí.
 */

const STORAGE_KEY = 'eldescuadre.pin';

/** Cuántas vueltas se le dan. Alto a propósito: ver arriba. */
const ITERATIONS = 200_000;

/** Lo mínimo que se acepta. Cuatro dígitos son 10.000 combinaciones. */
export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 8;

interface StoredPin {
  /** La sal, en base64. Distinta en cada dispositivo. */
  salt: string;
  /** La derivación, en base64. */
  hash: string;
  iterations: number;
  /** Cuándo se configuró, para poder avisar de un PIN viejo. */
  createdAt: string;
}

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(text: string): Uint8Array {
  return Uint8Array.from(atob(text), (char) => char.charCodeAt(0));
}

/**
 * Deriva el PIN.
 *
 * Se hace con `crypto.subtle`, que es lo que el navegador ofrece para esto. No se usa
 * un `hash` normal (tipo SHA-256 a secas) porque es demasiado rápido: probar 10.000
 * combinaciones con SHA-256 tarda lo que un parpadeo.
 */
async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-256' },
    key,
    256,
  );

  return toBase64(new Uint8Array(bits));
}

/** ¿Se puede usar? Sin `crypto.subtle` no hay PIN seguro, y uno inseguro no se pone. */
export function pinSupported(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
}

/** ¿Hay un PIN configurado en este dispositivo? */
export function hasPin(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Cuando se configuró el PIN, si hay uno. */
export function pinCreatedAt(): string | null {
  const stored = readStored();
  return stored?.createdAt ?? null;
}

function readStored(): StoredPin | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredPin>;
    if (!parsed.salt || !parsed.hash || typeof parsed.iterations !== 'number') return null;

    return {
      salt: parsed.salt,
      hash: parsed.hash,
      iterations: parsed.iterations,
      createdAt: parsed.createdAt ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Guarda un PIN nuevo.
 *
 * Se comprueba que sea solo dígitos y del largo correcto. Un «PIN» de letras no es un
 * PIN, y si se aceptara, la pantalla de bloqueo con teclado numérico sería inútil.
 */
export async function setPin(pin: string): Promise<void> {
  if (!pinSupported()) throw new Error('Este navegador no permite guardar un PIN seguro.');

  if (!/^\d+$/.test(pin)) throw new Error('El PIN solo puede tener números.');
  if (pin.length < PIN_MIN_LENGTH) {
    throw new Error(`El PIN debe tener al menos ${PIN_MIN_LENGTH} dígitos.`);
  }
  if (pin.length > PIN_MAX_LENGTH) {
    throw new Error(`El PIN no puede tener más de ${PIN_MAX_LENGTH} dígitos.`);
  }

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(pin, salt, ITERATIONS);

  const stored: StoredPin = {
    salt: toBase64(salt),
    hash,
    iterations: ITERATIONS,
    createdAt: new Date().toISOString(),
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

/** Comprueba un PIN. Devuelve si es correcto. */
export async function checkPin(pin: string): Promise<boolean> {
  const stored = readStored();
  if (!stored) return false;

  try {
    const candidate = await derive(pin, fromBase64(stored.salt), stored.iterations);

    // Comparación en tiempo constante.
    //
    // Comparar con `===` filtra información por el tiempo que tarda en fallar: quien
    // esté probando puede medir cuántos caracteres acertó. Aquí no hay red de por
    // medio, pero hacerlo bien no cuesta nada y hacerlo mal se copia.
    if (candidate.length !== stored.hash.length) return false;

    let difference = 0;
    for (let index = 0; index < candidate.length; index += 1) {
      difference |= candidate.charCodeAt(index) ^ stored.hash.charCodeAt(index);
    }

    return difference === 0;
  } catch {
    return false;
  }
}

/** Quita el PIN de este dispositivo. */
export function clearPin(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Si no se puede borrar, no hay nada más que hacer desde aquí.
  }
}

// ---------------------------------------------------------------------------
//  El estado del bloqueo
// ---------------------------------------------------------------------------

const UNLOCKED_KEY = 'eldescuadre.pin.unlocked';

/**
 * ¿Está desbloqueada la aplicación **en esta pestaña**?
 *
 * Se guarda en `sessionStorage` y no en `localStorage` a propósito: al cerrar el
 * navegador, la aplicación vuelve a pedir el PIN. Si se guardara de forma permanente,
 * el bloqueo solo serviría la primera vez y nunca más.
 */
export function isUnlocked(): boolean {
  try {
    return sessionStorage.getItem(UNLOCKED_KEY) === 'true';
  } catch {
    // Sin `sessionStorage` no se puede recordar el desbloqueo. Se pide el PIN cada
    // vez, que es lo prudente.
    return false;
  }
}

export function markUnlocked(): void {
  try {
    sessionStorage.setItem(UNLOCKED_KEY, 'true');
  } catch {
    // No poder recordarlo solo significa que se pedirá otra vez.
  }
}

export function markLocked(): void {
  try {
    sessionStorage.removeItem(UNLOCKED_KEY);
  } catch {
    // Nada que hacer.
  }
}
